<template>
	<v-container fluid class="gc-wrap pa-0">
		<v-progress-linear v-if="loading || parentLoading" class="gc-loading" color="primary" height="3" indeterminate />

		<div v-if="error && !loading" class="gc-empty">
			No fue posible consultar el gantt combinado.
		</div>
		<div v-else-if="!chartBars.length && !loading && !parentLoading" class="gc-empty">
			Sin datos para mostrar.
		</div>

		<div v-if="chartBars.length" class="gc-chart-wrap">
			<div class="gc-legend">
				<div class="gc-legend-item">
					<span class="gc-legend-dot gc-legend-dot--timeoff" />
					<span>Tiempo muerto</span>
				</div>
				<div class="gc-legend-item">
					<span class="gc-legend-dot gc-legend-dot--setup-change" />
					<span>Cambio de Molde</span>
				</div>
				<div class="gc-legend-item">
					<span class="gc-legend-dot gc-legend-dot--setup-adjust" />
					<span>Ajuste de procesos</span>
				</div>
				<div class="gc-legend-item">
					<span class="gc-legend-dot gc-legend-dot--production" />
					<span>Produccion</span>
				</div>
				<div class="gc-legend-item">
					<span class="gc-legend-dot gc-legend-dot--extra" />
					<span>Extra</span>
				</div>
				<div class="gc-legend-item">
					<span class="gc-legend-dot gc-legend-dot--delay" />
					<span>Retraso</span>
				</div>
				<div class="gc-legend-item">
					<span class="gc-legend-dot gc-legend-dot--inoperative" />
					<span>Inoperante</span>
				</div>
			</div>
			<apexchart type="rangeBar" :height="chartHeight" :options="chartOptions" :series="chartSeries" />
		</div>
	</v-container>
</template>

<script>
import axios from "axios";

export default {
	name: "GanttsCombinados",
	props: {
		refreshToken: {
			type: Number,
			default: 0,
		},
		parentLoading: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			loading: false,
			error: false,
			rawMachines: [],
			pendingRefresh: false,
		};
	},
	watch: {
		refreshToken: {
			immediate: true,
			handler() {
				this.fetchGanttData();
			},
		},
	},
	computed: {
		chartBars() {
			const machines = this.toArray(this.rawMachines);
			if (!machines.length) return [];

			const orderedMachines = machines
				.map((machine, index) => {
					const model = `${machine?.modeloMaquina || machine?.ModeloMaquina || machine?.nombreMaquina || machine?.NombreMaquina || `Maq ${index + 1}`}`;
					const theorics = this.toArray(machine?.fragmentosTeoricos || machine?.FragmentosTeoricos);
					const reals = this.toArray(machine?.fragmentosReales || machine?.FragmentosReales);
					const maxTheoricEnd = theorics.reduce((maxValue, fragment) => {
						return Math.max(maxValue, this.toTimeMs(fragment?.fin || fragment?.Fin));
					}, 0);
					const maxRealEnd = reals.reduce((maxValue, fragment) => {
						return Math.max(maxValue, this.toTimeMs(fragment?.fin || fragment?.Fin));
					}, 0);
					return {
						machine,
						model,
						index,
						maxEnd: Math.max(maxTheoricEnd, maxRealEnd),
					};
				})
				.sort((a, b) => {
					if (b.maxEnd !== a.maxEnd) return b.maxEnd - a.maxEnd;
					return `${a.model}`.localeCompare(`${b.model}`);
				});

			const globalMinStart = orderedMachines.reduce((minValue, item) => {
				const theorics = this.toArray(item?.machine?.fragmentosTeoricos || item?.machine?.FragmentosTeoricos);
				const reals = this.toArray(item?.machine?.fragmentosReales || item?.machine?.FragmentosReales);
				const fragments = [...theorics, ...reals];
				const localMin = fragments.reduce((innerMin, fragment) => {
					const startMs = this.toTimeMs(fragment?.inicio || fragment?.Inicio);
					if (startMs <= 0) return innerMin;
					return Math.min(innerMin, startMs);
				}, Number.MAX_SAFE_INTEGER);
				return Math.min(minValue, localMin);
			}, Number.MAX_SAFE_INTEGER);
			const gapStartMs = Number.isFinite(globalMinStart) && globalMinStart > 0 ? globalMinStart : Date.now();

			const bars = [];
			orderedMachines.forEach((item, machineOrder) => {
				const machine = item.machine;
				const machineModel = `${item.model}`;
				const machineId = this.toNumber(machine?.idMaquina ?? machine?.IdMaquina);
				const machineKey = machineId > 0 ? `${machineId}` : `${machineOrder + 1}`;

				const pushLaneBars = (fragmentsInput, lane) => {
					const fragments = this.toArray(fragmentsInput)
						.map((fragment, fragmentIndex) => ({ fragment, fragmentIndex }))
						.sort((a, b) => {
							const aStart = this.toTimeMs(a?.fragment?.inicio || a?.fragment?.Inicio);
							const bStart = this.toTimeMs(b?.fragment?.inicio || b?.fragment?.Inicio);
							if (aStart !== bStart) return aStart - bStart;
							return this.toTimeMs(a?.fragment?.fin || a?.fragment?.Fin) - this.toTimeMs(b?.fragment?.fin || b?.fragment?.Fin);
						});

					fragments.forEach(({ fragment, fragmentIndex }) => {
						const startMs = this.toTimeMs(fragment?.inicio || fragment?.Inicio);
						const endMs = this.toTimeMs(fragment?.fin || fragment?.Fin);
						if (startMs <= 0 || endMs <= startMs) return;

						const fragmentState = this.resolveFragmentState(fragment);
						const type = fragmentState.typeLabel;
						const operations = this.toArray(fragment?.operaciones || fragment?.Operaciones);
						const piecesExpected = operations.reduce(
							(sum, op) => sum + this.toNumber(op?.piezasEsperadas ?? op?.PiezasEsperadas ?? op?.piezasProducir ?? op?.PiezasProducir),
							0
						);
						const piecesOk = operations.reduce((sum, op) => sum + this.toNumber(op?.piezasOk ?? op?.PiezasOk), 0);
						const piecesNoOk = operations.reduce((sum, op) => sum + this.toNumber(op?.piezasNoOk ?? op?.PiezasNoOk), 0);

						const articleItems = Array.from(
							new Set(
								operations
									.map((op) => `${op?.articulo || op?.Articulo || ""}`.trim())
									.filter(Boolean)
							)
						);
						const moldItems = Array.from(
							new Set(
								operations
									.map((op) => `${op?.claveMolde || op?.ClaveMolde || ""}`.trim())
									.filter(Boolean)
							)
						);
						const areaItems = Array.from(
							new Set(
								operations
									.map((op) => `${op?.area || op?.Area || ""}`.trim())
									.filter(Boolean)
							)
						);

						bars.push({
							uid: `gantt-combined-${machineKey}-${lane}-${fragmentIndex}`,
							x: `${machineModel} - ${lane}`,
							y: [startMs, endMs],
							machineModel,
							lane,
							isSpacer: false,
							isRed: fragmentState.isRed,
							isTiempoMuerto: fragmentState.isTiempoMuerto,
							isMoldeAjuste: fragmentState.isMoldeAjuste,
							isCambioMolde: fragmentState.isCambioMolde,
							isAjusteProcesos: fragmentState.isAjusteProcesos,
							isProduccion: fragmentState.isProduccion,
							isExtra: fragmentState.isExtra,
							isRetraso: fragmentState.isRetraso,
							isParo: fragmentState.isParo,
							isInoperante: fragmentState.isInoperante,
							fragmentType: type,
							odt:
								`${fragment?.clave || fragment?.Clave || fragment?.claveODT || fragment?.ClaveODT || ""}`.trim() ||
								(this.toNumber(fragment?.idTrabajoOrden ?? fragment?.IdTrabajoOrden) > 0
									? `ODT ${this.toNumber(fragment?.idTrabajoOrden ?? fragment?.IdTrabajoOrden)}`
									: "Sin ODT"),
							areaLabel: areaItems.length === 1 ? areaItems[0] : areaItems.length > 1 ? "Multiples" : "-",
							areaItems,
							articleLabel: articleItems.length === 1 ? articleItems[0] : articleItems.length > 1 ? "Multiples" : "-",
							articleItems,
							moldLabel: moldItems.length === 1 ? moldItems[0] : moldItems.length > 1 ? "Multiples" : "-",
							moldItems,
							operationsCount: operations.length,
							piecesExpected,
							piecesOk,
							piecesNoOk,
							fillColor: this.getFragmentColor(fragmentState, lane),
						});
					});
				};

				pushLaneBars(machine?.fragmentosTeoricos || machine?.FragmentosTeoricos, "Teorico");
				pushLaneBars(machine?.fragmentosReales || machine?.FragmentosReales, "Real");

				const isLastMachine = machineOrder === orderedMachines.length - 1;
				if (!isLastMachine) {
					// Fila separadora invisible para abrir espacio entre maquinas.
					bars.push({
						uid: `gantt-gap-${machineKey}`,
						x: `__gap__${machineKey}`,
						y: [gapStartMs, gapStartMs + 1],
						isSpacer: true,
						fillColor: "rgba(0,0,0,0)",
						machineModel: machineModel,
						lane: "Gap",
					});
				}
			});

			return bars;
		},
		chartSeries() {
			return [
				{
					name: "Gantt combinado",
					data: this.chartBars,
				},
			];
		},
		chartHeight() {
			const categories = new Set(this.chartBars.map((item) => `${item.x || ""}`)).size;
			return Math.max(300, categories * 34 + 72);
		},
		chartOptions() {
			const vm = this;
			const nowMarkerMs = Date.now();
			return {
				chart: {
					type: "rangeBar",
					fontFamily: "Montserrat, Segoe UI, sans-serif",
					toolbar: {
						show: false,
						tools: {
							selection: false,
							zoom: false,
							zoomin: false,
							zoomout: false,
							pan: false,
							reset: false,
						},
					},
					selection: { enabled: false },
					zoom: { enabled: false },
				},
				plotOptions: {
					bar: {
						horizontal: true,
						rangeBarGroupRows: true,
						barHeight: "94%",
						borderRadius: 4,
					},
				},
				grid: {
					borderColor: "#e1e9f4",
					strokeDashArray: 4,
					yaxis: {
						lines: {
							show: false,
						},
					},
				},
				xaxis: {
					type: "datetime",
					labels: {
						datetimeUTC: false,
						style: {
							colors: "#4a5e7a",
							fontSize: "11px",
						},
					},
				},
				yaxis: {
					labels: {
						formatter(value) {
							const text = `${value || ""}`;
							if (text.startsWith("__gap__")) return "";
							return text;
						},
						style: {
							colors: "#2a3f5d",
							fontWeight: 700,
							fontSize: "12px",
						},
					},
				},
				dataLabels: { enabled: false },
				annotations: {
					xaxis: [
						{
							x: nowMarkerMs,
							borderColor: "#d64545",
							strokeDashArray: 4,
							label: {
								text: "Ahora",
								style: {
									background: "#d64545",
									color: "#ffffff",
									fontSize: "10px",
									fontWeight: 700,
								},
							},
						},
					],
				},
				tooltip: {
					custom({ seriesIndex, dataPointIndex, w }) {
						const point = w.config.series?.[seriesIndex]?.data?.[dataPointIndex] || {};
						if (point?.isSpacer) return "";
						const startMs = vm.toNumber(point?.y?.[0]);
						const endMs = vm.toNumber(point?.y?.[1]);
						const durationSec = endMs > startMs ? Math.round((endMs - startMs) / 1000) : 0;
						if (Boolean(point?.isTiempoMuerto)) {
							return `<div style="padding:8px 10px;font-size:12px;">
								<div><b>${point?.machineModel || "-"}</b> - ${point?.lane || "-"}</div>
								<div><b>Tipo:</b> Tiempo muerto</div>
								<div><b>Inicio:</b> ${vm.formatDateTime(startMs)}</div>
								<div><b>Fin:</b> ${vm.formatDateTime(endMs)}</div>
								<div><b>Duracion:</b> ${vm.formatDuration(durationSec)}</div>
							</div>`;
						}
						const areaHtml = vm.formatSummaryHtml(point?.areaLabel, point?.areaItems);
						const articleHtml = vm.formatSummaryHtml(point?.articleLabel, point?.articleItems);
						const moldHtml = vm.formatSummaryHtml(point?.moldLabel, point?.moldItems);
						return `<div style="padding:8px 10px;font-size:12px;">
							<div><b>${point?.machineModel || "-"}</b> - ${point?.lane || "-"}</div>
							<div><b>Tipo:</b> ${point?.fragmentType || "-"}</div>
							<div><b>ODT:</b> ${point?.odt || "-"}</div>
							<div><b>Area:</b> ${areaHtml}</div>
							<div><b>Articulo:</b> ${articleHtml}</div>
							<div><b>Molde:</b> ${moldHtml}</div>
							<div><b>Operaciones:</b> ${vm.formatNumber(point?.operationsCount)}</div>
							<div><b>Piezas esperadas:</b> ${vm.formatNumber(point?.piecesExpected)}</div>
							<div><b>Piezas OK:</b> ${vm.formatNumber(point?.piecesOk)}</div>
							<div><b>Piezas no OK:</b> ${vm.formatNumber(point?.piecesNoOk)}</div>
							<div><b>Inicio:</b> ${vm.formatDateTime(startMs)}</div>
							<div><b>Fin:</b> ${vm.formatDateTime(endMs)}</div>
							<div><b>Duracion:</b> ${vm.formatDuration(durationSec)}</div>
						</div>`;
					},
				},
				legend: { show: false },
				noData: { text: "Sin datos" },
			};
		},
	},
	methods: {
		async fetchGanttData() {
			if (this.loading) {
				this.pendingRefresh = true;
				return;
			}
			this.loading = true;
			this.error = false;
			try {
				const response = await axios.get("/MaquinaInstancias/GanttUnificadoPorMaquina");
				const payload = this.normalizeResponse(response);
				this.rawMachines = this.toArray(payload);
			} catch (error) {
				this.error = true;
				console.error("Error consultando GanttUnificadoPorMaquina:", error);
			} finally {
				this.loading = false;
				if (this.pendingRefresh) {
					this.pendingRefresh = false;
					this.fetchGanttData();
				}
			}
		},
		normalizeResponse(response) {
			const payload = response?.data;
			const result = payload?.Data || payload?.data || payload?.Result || payload?.result || payload || [];
			if (Array.isArray(result)) return result;
			const byMachine = result?.porMaquina || result?.PorMaquina;
			return Array.isArray(byMachine) ? byMachine : [];
		},
		toArray(value) {
			return Array.isArray(value) ? value : [];
		},
		toNumber(value) {
			const num = Number(value);
			return Number.isFinite(num) ? num : 0;
		},
		toTimeMs(value) {
			const raw = `${value || ""}`.trim();
			if (!raw) return 0;
			const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
			const ms = new Date(normalized).getTime();
			return Number.isFinite(ms) ? ms : 0;
		},
		toBool(value) {
			if (typeof value === "boolean") return value;
			if (typeof value === "number") return value === 1;
			if (typeof value === "string") {
				const normalized = value.trim().toLowerCase();
				return normalized === "true" || normalized === "1";
			}
			return false;
		},
		resolveFragmentState(fragment) {
			const tipo = `${fragment?.tipoFragmento || fragment?.TipoFragmento || ""}`.trim().toLowerCase();
			const isTiempoMuerto =
				this.toBool(fragment?.tiempoMuerto ?? fragment?.TiempoMuerto) ||
				this.toBool(fragment?.esTiempoMuerto ?? fragment?.EsTiempoMuerto) ||
				tipo === "tiempomuerto" ||
				tipo === "tiempo muerto";
			const isParo =
				this.toBool(fragment?.paro ?? fragment?.Paro) ||
				tipo === "paro" ||
				tipo.startsWith("paro ");
			const isInoperante =
				this.toBool(fragment?.inoperante ?? fragment?.Inoperante) ||
				tipo === "inoperante" ||
				tipo.startsWith("inoperante ");
			const isCambioMolde =
				this.toBool(fragment?.cambioMolde ?? fragment?.CambioMolde) ||
				tipo === "cambio de molde" ||
				tipo.includes("cambio de molde");
			const isAjusteProcesos =
				this.toBool(fragment?.ajusteProcesos ?? fragment?.AjusteProcesos) ||
				tipo === "ajuste de procesos" ||
				tipo.includes("ajuste de procesos");
			const isMoldeAjuste =
				this.toBool(fragment?.moldeAjuste ?? fragment?.MoldeAjuste) ||
				isCambioMolde ||
				isAjusteProcesos;
			const isProduccion =
				this.toBool(fragment?.produccion ?? fragment?.Produccion) ||
				tipo === "produccion";
			const isExtra =
				this.toBool(fragment?.extra ?? fragment?.Extra) ||
				tipo === "extra" ||
				tipo === "desfase";
			const isRetraso =
				this.toBool(fragment?.retraso ?? fragment?.Retraso) ||
				tipo === "retraso";
			let typeLabel = "Produccion";
			if (isTiempoMuerto) typeLabel = "Tiempo muerto";
			else if (isInoperante || isParo) typeLabel = "Inoperante";
			else if (isRetraso) typeLabel = "Retraso";
			else if (isExtra) typeLabel = "Extra";
			else if (isCambioMolde && isAjusteProcesos) typeLabel = "Cambio de Molde y ajuste de procesos";
			else if (isCambioMolde) typeLabel = "Cambio de Molde";
			else if (isAjusteProcesos) typeLabel = "Ajuste de procesos";
			else if (isMoldeAjuste) typeLabel = "Cambio de Molde y ajuste de procesos";
			else if (isProduccion) typeLabel = "Produccion";
			else if (tipo) typeLabel = `${fragment?.tipoFragmento || fragment?.TipoFragmento || "Produccion"}`;
			return {
				typeLabel,
				isTiempoMuerto,
				isInoperante,
				isParo,
				isMoldeAjuste,
				isCambioMolde,
				isAjusteProcesos,
				isProduccion,
				isExtra,
				isRetraso,
				isRed: this.toBool(fragment?.esRojo ?? fragment?.EsRojo),
			};
		},
		getFragmentColor(fragmentState, _lane) {
			if (fragmentState?.isTiempoMuerto) return "#7c4dff"; // morado
			if (fragmentState?.isInoperante || fragmentState?.isParo) return "#f472b6"; // rosa
			if (fragmentState?.isMoldeAjuste) {
				const moldeFueraTiempo =
					Boolean(fragmentState?.isExtra) ||
					Boolean(fragmentState?.isRetraso) ||
					Boolean(fragmentState?.isRed);
				if (fragmentState?.isCambioMolde && fragmentState?.isAjusteProcesos) {
					return moldeFueraTiempo ? "#6f4e37" : "#8b6b4a";
				}
				if (fragmentState?.isCambioMolde) {
					return moldeFueraTiempo ? "#6b4226" : "#8b5e3c";
				}
				if (fragmentState?.isAjusteProcesos) {
					return moldeFueraTiempo ? "#5c4033" : "#a47551";
				}
				return moldeFueraTiempo ? "#6f4e37" : "#8b6b4a";
			}
			if (fragmentState?.isRetraso) return "#d64545"; // rojo
			if (fragmentState?.isExtra) return "#f0ac22"; // amarillo
			if (fragmentState?.isMoldeAjuste || fragmentState?.isProduccion) return "#2e9d53"; // verde
			return "#2e9d53";
		},
		formatDateTime(value) {
			const ms = this.toNumber(value);
			if (ms <= 0) return "-";
			return new Date(ms).toLocaleString("es-MX", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				hour12: false,
			});
		},
		formatDuration(totalSeconds) {
			const safeSeconds = Math.max(0, Math.round(this.toNumber(totalSeconds)));
			const totalMinutes = Math.floor(safeSeconds / 60);
			const days = Math.floor(totalMinutes / 1440);
			const remainingAfterDays = totalMinutes % 1440;
			const hours = Math.floor(remainingAfterDays / 60);
			const minutes = remainingAfterDays % 60;
			return `${days}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`;
		},
		formatNumber(value) {
			const num = this.toNumber(value);
			const isInteger = Math.abs(num - Math.trunc(num)) < 0.000001;
			return num.toLocaleString("en-US", {
				minimumFractionDigits: 0,
				maximumFractionDigits: isInteger ? 0 : 1,
			});
		},
		escapeHtml(value) {
			const map = {
				"&": "&amp;",
				"<": "&lt;",
				">": "&gt;",
				'"': "&quot;",
				"'": "&#39;",
			};
			return `${value ?? ""}`.replace(/[&<>"']/g, (char) => map[char] || char);
		},
		formatSummaryHtml(summary, items, maxItems = 4) {
			const safeSummary = `${summary || "-"}`.trim() || "-";
			const uniqueItems = Array.from(
				new Set(
					this.toArray(items)
						.map((item) => `${item || ""}`.trim())
						.filter(Boolean)
				)
			);
			const normalizedSummary = safeSummary.toLowerCase();
			const isMultiple = normalizedSummary === "multiples";
			if (isMultiple && uniqueItems.length) {
				const visible = uniqueItems.slice(0, Math.max(1, this.toNumber(maxItems) || 4));
				const hiddenCount = Math.max(0, uniqueItems.length - visible.length);
				const list = visible
					.map((item) => `<li style="margin-left:14px;list-style:disc;">${this.escapeHtml(item)}</li>`)
					.join("");
				const extra = hiddenCount > 0
					? `<li style="margin-left:14px;list-style:disc;">+${this.formatNumber(hiddenCount)} mas</li>`
					: "";
				return `<span>Multiples</span><ul style="margin:2px 0 0 0;padding:0;">${list}${extra}</ul>`;
			}
			return this.escapeHtml(safeSummary);
		},
	},
};
</script>

<style scoped>
.gc-wrap {
	padding: 8px 8px 0;
}

.gc-loading {
	margin-bottom: 8px;
}

.gc-chart-wrap {
	background: #ffffff;
	border: 1px solid #e3ebf5;
	border-radius: 10px;
	padding: 10px;
}

.gc-legend {
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 8px 14px;
	margin-bottom: 8px;
}

.gc-legend-item {
	align-items: center;
	color: #35506f;
	display: inline-flex;
	font-size: 0.76rem;
	font-weight: 600;
	gap: 6px;
}

.gc-legend-dot {
	border-radius: 999px;
	display: inline-block;
	height: 10px;
	width: 10px;
}

.gc-legend-dot--timeoff {
	background: #7c4dff;
}

.gc-legend-dot--setup-change {
	background: #8b5e3c;
}

.gc-legend-dot--setup-adjust {
	background: #a47551;
}

.gc-legend-dot--production {
	background: #2e9d53;
}

.gc-legend-dot--extra {
	background: #f0ac22;
}

.gc-legend-dot--delay {
	background: #d64545;
}

.gc-legend-dot--inoperative {
	background: #f472b6;
}

.gc-empty {
	color: #6d7f97;
	font-size: 0.92rem;
	padding: 4px 0;
}
</style>
